// apps/admin-dashboard/src/pages/DiscographyPage.tsx
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Music } from 'lucide-react';
import type { DiscographyRelease } from '@yp/types';
import { useAuth } from '../context/AuthContext';
import { getAdminDiscography, deleteRelease } from '../lib/api';

const TYPE_LABELS: Record<string, string> = {
  single: 'Single', mini_album: 'Mini Album', album: 'Album',
  digital: 'Digital', collaboration: 'Collaboration',
};

export default function DiscographyPage() {
  const { accessToken } = useAuth();
  const [releases, setReleases] = useState<DiscographyRelease[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  async function fetchReleases() {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      setReleases(await getAdminDiscography(accessToken));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { fetchReleases(); }, [accessToken]);

  async function handleDelete(id: string, title: string) {
    if (!accessToken) return;
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await deleteRelease(accessToken, id);
      setReleases((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">Discography</h1>
          <p className="mt-0.5 text-sm text-gray-500">{releases.length} releases</p>
        </div>
        <Link to="/discography/new"
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--yp-pink)' }}>
          <Plus size={16} /> Add Release
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
        </div>
      ) : error ? (
        <p className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
      ) : releases.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center">
          <p className="text-gray-500">No releases yet.</p>
          <Link to="/discography/new" className="mt-2 inline-block text-sm text-pink-400 hover:underline">
            Add the first release
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {releases.map((release) => (
            <div key={release.id}
              className="flex items-center gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <div className="h-12 w-12 shrink-0 rounded-lg bg-black/30 overflow-hidden">
                {release.cover_url ? (
                  <img src={release.cover_url} alt={release.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <Music size={20} className="text-gray-600" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-100 truncate">{release.title}</p>
                  <span className="rounded px-1.5 py-0.5 text-[10px] bg-white/5 text-gray-400 shrink-0">
                    {TYPE_LABELS[release.type] ?? release.type}
                  </span>
                  {!release.is_published && (
                    <span className="rounded px-1.5 py-0.5 text-[10px] bg-yellow-500/10 text-yellow-500 shrink-0">
                      Draft
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {release.release_date ?? 'No date'} · {release.members?.length ?? 0} members
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to={`/discography/${release.id}/edit`}
                  className="flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-gray-400 hover:border-pink-500/30 hover:text-pink-400">
                  <Pencil size={12} /> Edit
                </Link>
                <button type="button" onClick={() => handleDelete(release.id, release.title)}
                  className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-gray-400 hover:border-red-500/30 hover:text-red-400">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
